import {
    all_dvs_printed,
    buildQuerySum,
    dailyBuildQuerySum,
    getCofcLink,
    getMassDocumentPdf,
    getOrderDocumentPdf,
    massSendFilesOf
} from 'api/customer/documents.api';
import {callbackNetworkResult, dataMimshak, dataToHash, getFile, getReportPdf} from 'api/customer/files.api';
import {
    collectRowExternalSoftwareDocs,
    hasExternalSoftwareIntegration,
    mergeRefreshedDocsIntoRow,
    sendAndRefreshDocsToExternalSoftware
} from 'utils/externalSoftware';
import Swal from "sweetalert2";
import {
    whatsapp2,
    userIcon,
    pdfIcon,
    duplicateIcon,
    deleteIcon,
    cancelIcon,
    addFilterIcon,
    addDocIcon,
    branchIcon,
    borderIcon,
    phoneIcon,
    pinIcon,
    columnIcon,
    rowIcon,
    email,
    boxIcon,
    antena
} from 'assets/images';
import JSDB, {NKTypeConvertor} from 'fromKotlin/nk';
import {dateObjectToString, dateObjectToStringIsrael} from 'fromKotlin/nkutils';
import xslx from "../../../assets/images";
import {parseJSON} from "date-fns";
import {distinctByField} from "../../../utils/sort";
import {appendCofcOrderShopLink, buildCofcOrderShopLink, encodeWhatsappShareText} from "../../../utils/whatsappShareUrl";
import {deserializeOrderDataToOrder, orderKey} from "../../../types/OrderData";
import {deserializeDocument, needsTypedFirstUnit} from "../../../types/DocumentsTypes/utils";
import {getSelectedRows} from "../../../ui-component/ag-grid/Ag-Table";
import {createPinnedRowDataNew} from "../../../ui-component/ag-grid/ag-grid-functions";
import {copyTransposedSelectionToClipboard} from "../../../ui-component/ag-grid/ag-table-utils";
import {AG_GRID_LOCALE_HE} from "../../../ui-component/ag-grid/ag-table-localization";
import {geocodeAddress} from "../../../api/customer/route_optimization.api";
import {aiRouteAssign} from "../../../api/customer/ai_assistent.api";
import {getRepLinkFlags} from "../../../utils/repLinkFlags";
import {getTranzilaConfig} from "../../../api/customer/tranzila.api";
import {collectPayableOrderDocs, groupPayableByClient, paymentSendPayload} from "../../../utils/paymentLinkDocs";

const ROUTE_ASSIGN_MIN_CONFIDENCE = 0.8;

/** Forced address (כתובת מאולצת) wins, else the client's city/street/number. */
const getRowDeliveryAddress = (row?: Record<string, any>) => {
    const forced = String(row?.forced_address ?? '').trim();
    if (forced) {
        const parts = forced.split(',');
        const structured = parts.length > 1;
        return {
            city: structured ? (parts[0]?.trim() ?? '') : forced,
            street: structured ? (parts[1]?.trim() ?? '') : '',
            number: structured ? (parts[2]?.trim() ?? '') : '',
            raw: forced,
        };
    }
    if (!row?.id) return {city: '', street: '', number: '', raw: ''};
    const client = JSDB().getClient(Number(row.id)).first;
    const city = String(client?.city ?? row?.city ?? '').trim();
    const street = String(client?.street ?? row?.street ?? '').trim();
    const number = String(client?.streetNumber ?? row?.streetNumber ?? '').trim();
    const raw = [city, street, number].filter(Boolean).join(',');
    return {city, street, number, raw};
};

const driverNameByAgentId = (agentId: number): string => {
    const byDriver = JSDB().getAgentBy(agentId, null, false, true, false, false)?.first;
    const name = String(byDriver?.user_name ?? '').trim();
    if (name) return name;
    const any = JSDB().getAgentBy(agentId, null, false, false, false, true)?.first;
    return String(any?.user_name ?? '').trim();
};

/**
 * Resolves each row's map point ("lat,lon" string), aligned 1:1 with [rows]: a static_data forced address
 * (כתובת מאולצת) is geocoded and overrides the client's own location, same rule as route optimization —
 * otherwise this falls back to the client's own geo location (JSDB().getGeoLocations).
 */
const resolveRowsGeoLocations = async (rows: any[]): Promise<string[]> => {
    const forcedPoints: Record<string, string> = {};
    await Promise.all(rows.map(async (row) => {
        const forced = (row?.forced_address ?? '').trim();
        if (!forced) return;
        const point = await geocodeAddress(forced);
        if (point) forcedPoints[orderKey(row)] = `${point.lat},${point.lon}`;
    }));
    const ids = rows.map((row) => row?.id ? Number(row.id) : 0);
    const clientLocations = JSDB().getGeoLocations(NKTypeConvertor().toKotlinList(ids))?.first?.toArray() ?? [];
    return rows.map((row, i) => forcedPoints[orderKey(row)] ?? clientLocations[i] ?? '');
};

/** Effective driver for export titles: נהג זמני > נהג > סוכן. */
const effectiveDriverName = (data: any) => {
    const forced = data?.orderDriver;
    if (forced != null && String(forced).trim() !== '') return String(forced).trim();
    const driver = data?.driverName;
    if (driver != null && String(driver).trim() !== '') return String(driver).trim();
    const agent = data?.agent;
    if (agent != null && String(agent).trim() !== '') return String(agent).trim();
    return '';
};

const addDriverToTitleHolder = (title_holder: Set<unknown>, data: any) => {
    const name = effectiveDriverName(data);
    if (name) title_holder.add(name);
};

/** Driver/agent title for the PDF header (comma-separated). */
const driverExportTitle = (title_holder: Set<unknown>) =>
    Array.from(title_holder).filter((t) => t != null && String(t).trim() !== '').join(',');

const driverNamesFromHolder = (title_holder: Set<unknown>) =>
    Array.from(title_holder)
        .filter((t) => t != null && String(t).trim() !== '')
        .map((t) => String(t).trim());

const sanitizeFilePart = (s: string) =>
    String(s).trim().replace(/\s+/g, '_').replace(/[\\/:*?"<>|]/g, '');

const exportDateStamp = (d?: Date | string) => {
    if (!d) return '';
    try {
        const dt = d instanceof Date ? d : parseJSON(d);
        if (!dt || Number.isNaN(dt.getTime())) return '';
        return dateObjectToString(dt);
    } catch {
        return '';
    }
};

/** One driver keeps the name; several become 3_נהגים so the download name stays short. */
const driverExportFileSuffix = (title_holder: Set<unknown>) => {
    const names = driverNamesFromHolder(title_holder);
    if (names.length === 0) return '';
    if (names.length === 1) return sanitizeFilePart(names[0]);
    return `${names.length}_נהגים`;
};

const driverExportFileName = (label: string, title_holder: Set<unknown>, fileDate?: Date | string) => {
    const suffix = driverExportFileSuffix(title_holder);
    const dateStamp = exportDateStamp(fileDate);
    return [sanitizeFilePart(label), suffix, dateStamp].filter(Boolean).join('_');
};

/** Delivery/invoice/visit-note rows for export. Visit notes without orderId inherit the grid row's order. */
const collectRowExportDocs = (rows: any[]) => {
    const docs: any[] = [];
    (rows || []).forEach((row) => {
        (row?.dvs || []).forEach((dv: any) => {
            if (!dv) return;
            const isVisit = dv.type === 'visit_note' || dv.header?.docType === 12;
            const orderId = dv.orderId ?? dv.header?.order_id ?? dv.nkObject?.order_id;
            if (isVisit && !(Number(orderId) > 0) && row.orderId) {
                docs.push({...dv, orderId: Number(row.orderId)});
            } else {
                docs.push(dv);
            }
        });
    });
    return docs;
};

/** Order ids + driver title for PDF reports (byIds=1 — each order keeps its own נהג זמני). */
const collectReportSelection = (rows: any[]) => {
    const orderIds: number[] = [];
    const title_holder = new Set<unknown>();
    rows.forEach((data) => {
        const hasRow = (data?.dvs && data?.dvs.length > 0) || (data?.orderId != '');
        if (hasRow && data?.orderId) orderIds.push(Number(data.orderId));
        if (hasRow) addDriverToTitleHolder(title_holder, data);
    });
    return { orderIds, title_holder };
};

const asReportDate = (value: unknown): Date | null => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === 'string' && value.trim()) {
        try {
            const parsed = parseJSON(value);
            if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) return parsed;
        } catch {
            return null;
        }
    }
    return null;
};

/** From/to for sheet PDF reports. A picked range uses both ends, even if the menu was built earlier. */
const reportDateSpan = (date: unknown, toDate: unknown, day: number) => {
    const single = day == -1
        ? (asReportDate(date) ?? new Date())
        : new Date(JSDB().getBaseDaysRange().toArray()[day]);
    const end = day == -1 ? asReportDate(toDate) : null;
    let from = single;
    let to = end ?? single;
    if (dateObjectToString(to) < dateObjectToString(from)) {
        const swap = from;
        from = to;
        to = swap;
    }
    const fromKey = dateObjectToString(from);
    const toKey = dateObjectToString(to);
    return {
        from,
        to,
        fileDates: fromKey === toKey ? fromKey : `${fromKey}_${toKey}`,
    };
};

/** Client ids + driver title for legacy PDF reports (no byIds — aggregates by client for the date). */
const collectClientReportSelection = (rows: any[]) => {
    const clientIds: number[] = [];
    const title_holder = new Set<unknown>();
    rows.forEach((data) => {
        const hasRow = (data?.dvs && data?.dvs.length > 0) || (data?.orderId != '');
        if (hasRow && data?.id != null) clientIds.push(Number(data.id));
        if (hasRow) addDriverToTitleHolder(title_holder, data);
    });
    return { clientIds: [...new Set(clientIds)], title_holder };
};

/** Delivery note ids + driver title for PDF reports (byIds=1 — each תעודה keeps its own נהג זמני). */
const collectDeliveryReportSelection = (rows: any[]) => {
    const deliveryIds: number[] = [];
    const title_holder = new Set<unknown>();
    rows.forEach((data) => {
        if (data?.dvs?.length > 0) {
            let rowHasDv = false;
            data.dvs.forEach((dd: any) => {
                if (dd?.type === 'delivery_certificate' && dd?.status !== 'canceled' && dd?.id != null) {
                    deliveryIds.push(Number(dd.id));
                    rowHasDv = true;
                }
            });
            if (rowHasDv) addDriverToTitleHolder(title_holder, data);
        }
    });
    return { deliveryIds, title_holder };
};

/** Order ids from rows that already have תעודות — for השוואת כמויות with byIds (orders + linked deliveries). */
const collectOrderReportSelectionFromDeliveryRows = (rows: any[]) => {
    const orderIds: number[] = [];
    const title_holder = new Set<unknown>();
    rows.forEach((data) => {
        if (data?.dvs?.length > 0 && data?.orderId) {
            orderIds.push(Number(data.orderId));
            addDriverToTitleHolder(title_holder, data);
        }
    });
    return { orderIds, title_holder };
};

/**
 * A line bulk הנפקה would drop: nothing in the first unit, only a second unit without conversion
 * or a third unit. Checks the lines newAllOrderNote sends: the saved order's lines unless the row
 * was edited, then the grid row itself (new products, blank clients).
 */
const rowNeedsTypedFirstUnit = (row: any, updatedRowIds?: Set<string>): boolean => {
    if (!row || row.readonly) return false;
    // -1 is a visit client: bulk הנפקה creates a visit note, not a delivery/invoice.
    if (JSDB().getClient(Number(row.id)).first?.getNotes?.() === -1) return false;
    const saved = row.nkObject?.delivery_info;
    const lines = !updatedRowIds?.has(orderKey(row)) && saved?.size > 0
        ? saved.toArray()
        : (deserializeOrderDataToOrder(row)?.toArray?.() ?? []);
    return lines.some((pd: any) => needsTypedFirstUnit({
        quantity: pd.value,
        quantityReturns: pd.returns,
        quantitySecond: pd.wrapped_amount,
        quantityThird: pd.third_amount,
        conversion_ratio: pd.conversion_ratio,
    }));
};

/** The document form for one saved order, with the lines that need a first-unit amount shown. */
const issueFormFromOrder = (clientId: string | number, doc: any) => {
    const clientNotes = JSDB().getClient(Number(clientId)).first?.getNotes?.();
    const rawDate = doc.date;
    const parsed = rawDate instanceof Date ? rawDate : (rawDate ? new Date(rawDate) : null);
    return {
        customerId: String(clientId),
        type: clientNotes === 0 ? 'invoice' as const : 'delivery_certificate' as const,
        products: ((doc.products ?? []) as any[]).map((p) => (needsTypedFirstUnit(p) ? {...p, forceShow: true} : p)),
        refId: [Number(doc.id)],
        date: parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined,
    };
};

const sheetPdfMenuItems = (date, toDate, day, hideContextMenu) => {
    const span = reportDateSpan(date, toDate, day);
    const item = (name: string, reportType: string) => ({
        name,
        icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
        action() {
            getFile(
                false,
                () => getReportPdf(span.from, span.to, reportType),
                `${name} ${span.fileDates}.pdf`,
                'application/octet-stream',
                undefined,
                undefined,
            );
            hideContextMenu();
        },
    });
    return [
        item('דוח איסוף הזמנות', 'DATA_TO_COLLECTION_ORDERS'),
        item('דוח קרטונים לפי נהג', 'DATA_TO_DRIVER_KARTON'),
        item('דוח קרטונים לפי נהג ומוצר', 'DATA_TO_DRIVER_KARTON_PRODUCT'),
        ...(JSDB().getCompany() === 'klinzshivuk' ? [item('דוח קלינץ — סיכום לפי מוצר', 'DATA_TO_KLINZ')] : []),
    ];
};

const contextMenu2 = (params, setTempData, removeAllFilters, onApplyFilter, addCustomerFilter, activeState, dvs_flag, updatedRowIds, saveOrder, cancelDV, date, theme, deleteOrder, updatedDriverRowIds, setSingleDelete, setSelectedCustomersSend, setDistributeDialogOn, setReorderDialog, newClientOrder, setDocumentAction, setOpenSelectProductsToExport, setIdsForDuplicate, setExternalAnchor,pinnedControl,columns, setClientTransferAction, setDriverLinkRows, setPersistentDriverLinkRows, routeAssignEnabled, setIssueFromOrder, toDate?: Date | null) => {

    const ranges = params?.api?.getCellRanges();

    const selected = getSelectedRows(params?.api)

    let nodesInRange: any = [];
    selected?.forEach((row) => {
        nodesInRange.push(row);
    })

    if (!selected?.length) {
        ranges?.forEach((range) => {
            const rowMin = range?.startRow?.rowIndex < range?.endRow?.rowIndex ? range?.startRow?.rowIndex : range?.endRow?.rowIndex;
            const rowMax = range?.startRow?.rowIndex > range?.endRow?.rowIndex ? range?.startRow?.rowIndex : range?.endRow?.rowIndex;
            for (let i = rowMin; i <= rowMax; i += 1) {
                const node = params.api?.getDisplayedRowAtIndex(i);
                if (node?.data) {
                    nodesInRange.push(node?.data)
                }
                if (node?.group) {
                    const children = node?.childrenAfterGroup;
                    children?.forEach((child) => {
                        nodesInRange.push(child?.data)
                    })
                }
            }
        })
    }
    nodesInRange = distinctByField(nodesInRange, orderKey)
    let notes = true;
    nodesInRange.forEach((data) => {
        notes = notes && (data?.readonly);
    })

    const range = ranges?.[0];
    const rowIndex = range?.startRow.rowIndex;
    const rowData = nodesInRange[0]

    let withBox = false;
    let total = 0;
    let boxes = 0;
    if (range?.columns?.length === 1)
        nodesInRange.forEach((data) => {
            if (range?.startColumn?.colDef?.field?.startsWith('p_')) {
                const id = Number(range?.startColumn?.colDef?.field?.replace('p_', ''));
                const product = JSDB().getClientProduct(id).first;
                const value = Number(data?.[range?.startColumn?.colDef?.field]);
                if (!isNaN(value)) {
                    if (data?.[range?.startColumn?.colDef?.field] && data?.[range?.startColumn?.colDef?.field.replace('p_', 'u_')]) {
                        withBox = true;
                        total += data?.[range?.startColumn?.colDef?.field] * product?.getOrderProduct()?.conversion_rate ?? 0 // add convertion rate
                        boxes += data?.[range?.startColumn?.colDef?.field]
                    } else {
                        total += data?.[range?.startColumn?.colDef?.field]
                    }
                }
            }
        })
    const withBoxText = withBox ? total > 0 ? `${range?.startColumn?.colDef?.headerName}:  ${total} = ${boxes}` : `${boxes}` : `${range?.startColumn?.colDef?.headerName}: ${total}`;

    const selectedCustomers = nodesInRange;
    const freezeableSelectedCustomers = selectedCustomers.filter((data) =>
        !data?.readonly &&
        data?.orderId != undefined &&
        data?.orderId !== '' &&
        !(data?.dvs && data?.dvs.length > 0) &&
        (dvs_flag || !updatedRowIds.has(orderKey(data)))
    );

    const isProduct = range?.startColumn?.colDef?.field?.startsWith('p_');
    const tableDividers = JSON.parse(localStorage.getItem('orders-table-dividers') || '[]');
    const colId = range?.startColumn?.colDef?.field;
    const optDate = activeState.day==-1 ? date: new Date(JSDB().getBaseDaysRange().toArray()[activeState.day])
    // Never call close() — that is window.close() and Chrome will kill the tab
    // if it was opened by script (second tab, duplicate, target=_blank, etc.).
    const hideContextMenu = () => {
        params?.api?.hidePopupMenu?.();
    };

    const whatsappCallBack = (result) => {
        const client = JSDB().getClient(Number(rowData.id)).first
        let text = JSDB().userCofcConf?.text && JSDB().userCofcConf?.text != "None" ? JSDB().userCofcConf?.text : `שלום ${"#name"} ${"#id"}אנא בצע הזמנה עבור ${JSDB().getUser().first.user_company_print_name}`
        text = text.replace("#name", client.getName())
        text = text.replace("#id", client.id)
        text = text.replace("#external_id", client.getExternalId())
        text = text.replace("#date", dateObjectToStringIsrael(date))
        text = text.replace("#day", JSDB().getDatesManipulator().getDayOfWeekStr(dateObjectToString(date), true))

        const url = encodeWhatsappShareText(appendCofcOrderShopLink(text, result, dateObjectToString(date)));
        const phone = client.getPhone();
        window?.open(`https://api.whatsapp.com/send?text=${url}&phone=${JSDB().getValidPhone(phone)}`, '_blank')?.focus();
    };

    const contextMenu = [
        {
            name: `לקוחות מסומנים: <b>${selectedCustomers.length || nodesInRange.size || 0}</b>`,
            icon: `<img src="${userIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
        },
        "separator",
        {
            name: !notes && activeState.day == -1 ? "הנפק תעודת משלוח" : notes && activeState.day == -1 ? "הסר תעודת משלוח" : "הנפק תעודת משלוח",
            icon: `<img src="${addDocIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
            cssClasses: ["context-menu-red-item"],
            action() {
                if (!notes && activeState.day == -1) {
                    const orders: any = [];
                    selectedCustomers.forEach((data) => {
                        if (!(data?.dvs && data?.dvs.length > 0)) orders.push(data);

                    })

                    // Bulk הנפקה drops lines with no first-unit amount. One such order opens the
                    // document form; with several, those orders are left out and listed.
                    const needTyping = orders.filter((row) => rowNeedsTypedFirstUnit(row, updatedRowIds));
                    if (orders.length === 1 && needTyping.length === 1 && setIssueFromOrder) {
                        hideContextMenu();
                        const row = orders[0];
                        if (row.orderId && row.nkObject && !updatedRowIds.has(orderKey(row))) {
                            const doc = deserializeDocument(row.nkObject);
                            if (doc?.type === 'order') setIssueFromOrder(issueFormFromOrder(row.id, doc));
                            return;
                        }
                        // Unsaved edits or no order yet: save first, so the form opens on the saved
                        // order (server prices, linked order id).
                        saveOrder([orderKey(row)], undefined, undefined, undefined, {
                            onSaved: (docs) => {
                                const doc = docs.find((d) => d?.type === 'order' && String(d.entId) === String(row.id));
                                if (doc) setIssueFromOrder(issueFormFromOrder(row.id, doc));
                            },
                        });
                        return;
                    }
                    const toIssue = orders.filter((row) => !needTyping.includes(row));
                    const dvs = toIssue.map(() => null);
                    const skippedText = needTyping.length > 0
                        ? `לא יונפקו — יש מוצר בלי כמות ביחידה הראשונה, הנפק כל אחד בנפרד: ${needTyping.map((r) => r.customer || r.id).join(', ')}`
                        : undefined;
                    const text = dvs.length === 1 ? `האם תרצה להנפיק תעודת משלוח ללקוח ${toIssue[0]?.customer}?` : `האם תרצה להנפיק ${dvs.length} תעודות משלוח?`;
                    if (dvs.length > 0)
                        Swal.fire({
                            title: text,
                            text: skippedText,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: theme.palette.primary.main,
                            cancelButtonColor: theme.palette.error.main,
                            confirmButtonText: 'הנפק',
                            cancelButtonText: 'בטל'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                /* Perform insert, update, and delete to the database here */
                                saveOrder(toIssue.map((p) => orderKey(p)), dvs);
                            }
                        });
                    else if (skippedText)
                        Swal.fire({title: 'לא הונפקו תעודות', text: skippedText, icon: 'warning'});
                    hideContextMenu();
                }
                if (notes && activeState.day == -1) {
                    const dvs: any = [];
                    selectedCustomers.forEach((data) => {

                        if (data?.dvs && data?.dvs.length > 0)
                            data?.dvs?.forEach((dd) => (dd?.status !== 'closed' && dd?.type == 'delivery_certificate') ? dvs.push(dd?.id) : undefined);

                    })
                    const text = dvs?.length === 1 ? `האם תרצה לבטל תעודת משלוח ללקוח ${selectedCustomers[0]?.customer}?` : `האם תרצה לבטל ${dvs.length} תעודות משלוח?`;
                    if (dvs.length > 0)
                        Swal.fire({
                            title: text,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: theme.palette.primary.main,
                            cancelButtonColor: theme.palette.error.main,
                            confirmButtonText: 'ביטול תעודות',
                            cancelButtonText: 'חזור'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                /* Perform insert, update, and delete to the database here */
                                cancelDV(dvs);
                            }
                        });
                    hideContextMenu();
                }
            },
        },
        "separator",
        {
            name: "פעולות ייצוא",
            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
            subMenu: [
                {
                    name: "ייצא הזמנות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const update_printed = new Set();
                        const title_holder = new Set<unknown>();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)
                            if (data?.orderId) addDriverToTitleHolder(title_holder, data)

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0) {
                            getFile(false, () => getOrderDocumentPdf(undefined, curPdf), driverExportFileName('הזמנות', title_holder, optDate));
                            setTempData((prev) =>
                                prev.map((d, i) => {
                                        if (update_printed.has(d.orderId)) {
                                            updatedRowIds.add(orderKey(d));
                                            return {...d, status: 'הודפס', hasBeenEdited: true};
                                        } else {
                                            return d;
                                        }

                                    }
                                )
                            );
                            setTimeout(() => {
                                params.api.refreshCells({force: true});
                            }, 50)

                        }

                        hideContextMenu();
                    },
                },
                {
                    name: "הדפס הזמנות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const update_printed = new Set();
                        const title_holder = new Set<unknown>();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)
                            if (data?.orderId) addDriverToTitleHolder(title_holder, data)

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0) {
                            getFile(true, () => getOrderDocumentPdf(undefined, curPdf), driverExportFileName('הזמנות', title_holder, optDate));
                            setTempData((prev) =>
                                prev.map((d, i) => {
                                        if (update_printed.has(d.orderId)) {
                                            updatedRowIds.add(orderKey(d));
                                            return {...d, status: 'הודפס', hasBeenEdited: true};
                                        } else {
                                            return d;
                                        }

                                    }
                                )
                            );
                            setTimeout(() => {
                                params.api.refreshCells({force: true});
                            }, 50)

                        }

                        hideContextMenu();
                    },
                },
                {
                    name: "הדפס לליקוט",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const update_printed = new Set();
                        const title_holder = new Set<unknown>();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)
                            if (data?.orderId) addDriverToTitleHolder(title_holder, data)

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0) {
                            getFile(true, () => getMassDocumentPdf(undefined, curPdf, curPdf.map(() => 10), undefined, undefined, undefined, undefined, undefined, true), driverExportFileName('ליקוט', title_holder, optDate));
                            setTempData((prev) =>
                                prev.map((d, i) => {
                                        if (update_printed.has(d.orderId)) {
                                            updatedRowIds.add(orderKey(d));
                                            return {...d, status: 'הודפס', hasBeenEdited: true};
                                        } else {
                                            return d;
                                        }

                                    }
                                )
                            );
                            setTimeout(() => {
                                params.api.refreshCells({force: true});
                            }, 50)

                        }

                        hideContextMenu();
                    },
                },

                {
                    name: "שלח הזמנות למייל",
                    icon: `<img src="${email}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const clients: any = [];
                        const update_printed = new Set();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId) clients.push(data?.id)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0) {
                            setSelectedCustomersSend({
                                selected: clients.map((u) => Number(u)),
                                defaultPlatform: 'email',
                                files: {
                                    ids: ids.map((u) => Number(u)),
                                    types: ids.map((p) => 10),
                                },
                                msg: '#docs',
                                subject: 'שליחת מסמכים',


                            })
                        }

                        hideContextMenu();
                    },
                },
                {
                    name: "שלח הזמנות לווטסאפ",
                    icon: `<img src="${whatsapp2}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const clients: any = [];
                        const update_printed = new Set();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId) clients.push(data?.id)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)

                        })

                        const curPdf = ids.map((u) => Number(u));

                        if (curPdf.length > 0) {
                            setSelectedCustomersSend({
                                selected: clients.map((u) => Number(u)),
                                defaultPlatform: 'whatsapp',
                                files: {
                                    ids: ids.map((u) => Number(u)),
                                    types: ids.map((p) => 10),
                                },
                                msg: '#docs',
                                subject: 'שליחת מסמכים',


                            })
                        }

                        hideContextMenu();
                    },
                },
                "separator",
                {
                    name: "ייצא תעודות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const title_holder = new Set<unknown>();
                        console.log(selectedCustomers)
                        selectedCustomers.forEach((data) => {
                            if (data?.dvs && data?.dvs.length > 0) {
                                ids.push(...data?.dvs);
                                addDriverToTitleHolder(title_holder, data);
                            }

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0)
                            getFile(false, () => getMassDocumentPdf(ids, undefined, undefined), driverExportFileName('תעודות', title_holder, optDate), undefined, async () => {
                                try {
                                    const x = await all_dvs_printed(ids);
                                    const set = new Set(x.map((dv) => dv.entId));
                                    setTempData((prev) =>
                                        prev.map((d, i) => {
                                                if (set.has((d.id))) {
                                                    return {...d, all_dvs_printed: true};
                                                } else {
                                                    return d;
                                                }

                                            }
                                        )
                                    );
                                } catch (e) {
                                }

                                setTimeout(() => {
                                    params.api.refreshCells({force: true});
                                }, 50)
                            });
                        hideContextMenu();
                    }
                },
                {
                    name: "הדפס תעודות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const title_holder = new Set<unknown>();
                        selectedCustomers.forEach((data) => {
                            if (data?.dvs && data?.dvs.length > 0) {
                                ids.push(...data?.dvs);
                                addDriverToTitleHolder(title_holder, data);
                            }

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0)
                            getFile(true, () => getMassDocumentPdf(ids, undefined, undefined), driverExportFileName('תעודות', title_holder, optDate), undefined, async () => {
                                try {
                                    const x = await all_dvs_printed(ids);
                                    const set = new Set(x.map((dv) => dv.entId));
                                    setTempData((prev) =>
                                        prev.map((d, i) => {
                                                if (set.has((d.id))) {
                                                    return {...d, all_dvs_printed: true};
                                                } else {
                                                    return d;
                                                }

                                            }
                                        )
                                    );
                                } catch (e) {
                                }

                                setTimeout(() => {
                                    params.api.refreshCells({force: true});
                                }, 50)
                            });
                        hideContextMenu();
                    }
                },
                {
                    name: "שלח תעודות לאימייל",
                    icon: `<img src="${email}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const docs = collectRowExportDocs(selectedCustomers);
                        if (docs.length > 0)
                            setSelectedCustomersSend({
                                selected: docs.map((u) => Number(u.entId)),
                                defaultPlatform: 'email',
                                files: massSendFilesOf(docs),
                                msg: '#docs',
                                subject: 'מסמך',
                            })
                        hideContextMenu();
                    }
                },
                {
                    name: "שלח תעודות לווטסאפ",
                    icon: `<img src="${whatsapp2}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const docs = collectRowExportDocs(selectedCustomers);
                        if (docs.length > 0) {
                            setSelectedCustomersSend({
                                selected: docs.map((u) => Number(u.entId)),
                                defaultPlatform: 'whatsapp',
                                files: massSendFilesOf(docs),
                                msg: '#docs',
                                subject: 'מסמך',
                            })
                        }
                        hideContextMenu();
                    }
                },
                ...(getTranzilaConfig() ? [{
                    name: "תשלום בלינק",
                    icon: `<img src="${whatsapp2}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const groups = groupPayableByClient(collectPayableOrderDocs(selectedCustomers));
                        hideContextMenu();
                        if (!groups.length) {
                            Swal.fire({
                                icon: 'info',
                                title: 'אין תעודות לתשלום',
                                text: 'סמן הזמנות עם תעודת משלוח/חשבונית שיש להן יתרה פתוחה.',
                            });
                            return;
                        }
                        setSelectedCustomersSend({
                            ...paymentSendPayload(groups),
                        });
                    },
                }] : []),
                ...(hasExternalSoftwareIntegration() ? [{
                    name: "שלח לתוכנה חיצונית",
                    icon: `<img src="${antena}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const docs: any[] = [];
                        selectedCustomers.forEach((data) => {
                            docs.push(...collectRowExternalSoftwareDocs(data));
                        });
                        if (docs.length > 0) {
                            callbackNetworkResult(() => sendAndRefreshDocsToExternalSoftware(docs), (refreshed) => {
                                selectedCustomers.forEach((data) => {
                                    Object.assign(data, mergeRefreshedDocsIntoRow(data, refreshed || []));
                                });
                                Swal.fire({
                                    title: 'נשלח לתוכנה חיצונית',
                                    icon: 'success',
                                    confirmButtonText: 'סגור'
                                });
                                params.api.refreshCells({force: true});
                            });
                        }
                        hideContextMenu();
                    }
                }] : []),
                {
                    name: "ייצא תעודות מקור+העתק",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const title_holder = new Set<unknown>();
                        selectedCustomers.forEach((data) => {
                            if (data?.dvs && data?.dvs.length > 0) {
                                ids.push(...data?.dvs);
                                addDriverToTitleHolder(title_holder, data);
                            }

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0)
                            getFile(false, () => getMassDocumentPdf(ids, undefined, undefined, undefined, 3), driverExportFileName('תעודות', title_holder, optDate), undefined, async () => {
                                try {
                                    const x = await all_dvs_printed(ids);
                                    const set = new Set(x.map((dv) => dv.entId));
                                    setTempData((prev) =>
                                        prev.map((d, i) => {
                                                if (set.has((d.id))) {
                                                    return {...d, all_dvs_printed: true};
                                                } else {
                                                    return d;
                                                }

                                            }
                                        )
                                    );
                                } catch (e) {
                                }

                                setTimeout(() => {
                                    params.api.refreshCells({force: true});
                                }, 50)
                            });
                        hideContextMenu();
                    }
                },
            ]
        },
        {
            name: "סיכומים",
            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
            subMenu: [
                {
                    name: "ייצא השוואת כמויות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    subMenu: [
                        {
                            name: "השוואה מלאה",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { clientIds: curPdf, title_holder } = collectClientReportSelection(selectedCustomers);
                                const title = Array.from(title_holder).join(',');
                                if (curPdf.length > 0)
                                    getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true), 'השוואת כמויות');
                                hideContextMenu();
                            }
                        },
                        {
                            name: "חוסרים",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            subMenu: [
                                {
                                    name: "יחידות (הזמנה/תעודה/פער)",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    action() {
                                        const { clientIds: curPdf, title_holder } = collectClientReportSelection(selectedCustomers);
                                        const title = Array.from(title_holder).join(',');
                                        if (curPdf.length > 0)
                                            getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, undefined, undefined, undefined, undefined, 1, true), 'חוסרים — יחידות');
                                        hideContextMenu();
                                    }
                                },
                                {
                                    name: "לפי לקוח",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    action() {
                                        const { clientIds: curPdf, title_holder } = collectClientReportSelection(selectedCustomers);
                                        const title = Array.from(title_holder).join(',');
                                        if (curPdf.length > 0)
                                            getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, undefined, undefined, undefined, undefined, 2, true), 'חוסרים — לפי לקוח');
                                        hideContextMenu();
                                    }
                                },
                                {
                                    name: "גלובלי לפי הזמנה",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    action() {
                                        const { clientIds: curPdf, title_holder } = collectClientReportSelection(selectedCustomers);
                                        const title = Array.from(title_holder).join(',');
                                        if (curPdf.length > 0)
                                            getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, undefined, undefined, undefined, undefined, 3, true), 'חוסרים — גלובלי לפי הזמנה');
                                        hideContextMenu();
                                    }
                                },
                                {
                                    name: "לקוח + יחידות בהזמנה",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    action() {
                                        const { clientIds: curPdf, title_holder } = collectClientReportSelection(selectedCustomers);
                                        const title = Array.from(title_holder).join(',');
                                        if (curPdf.length > 0)
                                            getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, undefined, undefined, undefined, undefined, 4, true), 'חוסרים — לקוח ויחידות בהזמנה');
                                        hideContextMenu();
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    name: "ייצא סיכום הזמנות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0)
                            getFile(false, () => buildQuerySum(curPdf, optDate, optDate, title, false, true, undefined, undefined, 1), driverExportFileName('סיכום הזמנות', title_holder, optDate));
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום הזמנות + סיכום נהגים מפורט",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0) {
                            // One PDF, per driver: סיכום הזמנות then סיכום נהגים מפורט (seperate=12).
                            getFile(
                                false,
                                () => dailyBuildQuerySum(curPdf, date, 1, undefined, 2, undefined, title, 12, 1),
                                driverExportFileName('סיכום הזמנות ונהגים מפורט', title_holder, optDate)
                            );
                        }
                        hideContextMenu();
                    }
                },
                ...sheetPdfMenuItems(date, toDate, activeState?.day, hideContextMenu),
                {
                    name: "ייצא סיכום נהגים",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0)
                            getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, undefined, 1, undefined, title, undefined, 1), driverExportFileName('סיכום נהגים', title_holder, optDate));
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום נהגים מפורט",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0)
                            getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, undefined, 2, undefined, title, undefined, 1), driverExportFileName('סיכום נהגים מפורט', title_holder, optDate));
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום הזמנות מפורט כללי",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        if (curPdf.length > 0)
                            getFile(false, () => dailyBuildQuerySum(curPdf, date, 4, undefined, 2, undefined, '', undefined, 1), driverExportFileName('ייצא סיכום הזמנות מפורט כללי', title_holder, optDate));
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום נהגים בטבלה",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0)
                            getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, undefined, 1, undefined, title, 10, 1), driverExportFileName('סיכום נהגים', title_holder, optDate));
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום נהגים לפי מוצר",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const { orderIds: curPdf, title_holder } = collectReportSelection(selectedCustomers);
                        const title = driverExportTitle(title_holder);
                        if (curPdf.length > 0)
                            getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, undefined, 2, undefined, title, 3, 1), driverExportFileName('סיכום נהגים לפי מוצר', title_holder, optDate), "application/pdf");
                        hideContextMenu();
                    }
                },
                {
                    name: "ייצא סיכום הזמנות ספציפי לפי מוצר",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        setOpenSelectProductsToExport({selectedCustomers: selectedCustomers,detailed: 2});
                    }
                },
                {
                    name: "ייצא סיכום הזמנות ספציפי",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        setOpenSelectProductsToExport({selectedCustomers: selectedCustomers});
                    }
                },
                {
                    name: "ייצא סיכום הזמנות ספציפי מפורט ספציפי",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        setOpenSelectProductsToExport({selectedCustomers: selectedCustomers, detailed: 1});
                    }
                },
                {
                    name: "סיכומים לפי תעודות",
                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    subMenu: [
                        {
                            name: "ייצא השוואת כמויות",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            subMenu: [
                                {
                                    name: "השוואה מלאה",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    action() {
                                        const { orderIds: curPdf, title_holder } = collectOrderReportSelectionFromDeliveryRows(selectedCustomers);
                                        const title = Array.from(title_holder).join(',');
                                        if (curPdf.length > 0)
                                            getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, 1), 'השוואת כמויות תעודות');
                                        hideContextMenu();
                                    }
                                },
                                {
                                    name: "חוסרים",
                                    icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                    subMenu: [
                                        {
                                            name: "יחידות (הזמנה/תעודה/פער)",
                                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                            action() {
                                                const { orderIds: curPdf, title_holder } = collectOrderReportSelectionFromDeliveryRows(selectedCustomers);
                                                const title = Array.from(title_holder).join(',');
                                                if (curPdf.length > 0)
                                                    getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, 1, undefined, undefined, undefined, 1, true), 'חוסרים תעודות — יחידות');
                                                hideContextMenu();
                                            }
                                        },
                                        {
                                            name: "לפי לקוח",
                                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                            action() {
                                                const { orderIds: curPdf, title_holder } = collectOrderReportSelectionFromDeliveryRows(selectedCustomers);
                                                const title = Array.from(title_holder).join(',');
                                                if (curPdf.length > 0)
                                                    getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, 1, undefined, undefined, undefined, 2, true), 'חוסרים תעודות — לפי לקוח');
                                                hideContextMenu();
                                            }
                                        },
                                        {
                                            name: "גלובלי לפי הזמנה",
                                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                            action() {
                                                const { orderIds: curPdf, title_holder } = collectOrderReportSelectionFromDeliveryRows(selectedCustomers);
                                                const title = Array.from(title_holder).join(',');
                                                if (curPdf.length > 0)
                                                    getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, 1, undefined, undefined, undefined, 3, true), 'חוסרים תעודות — גלובלי לפי הזמנה');
                                                hideContextMenu();
                                            }
                                        },
                                        {
                                            name: "לקוח + יחידות בהזמנה",
                                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                                            action() {
                                                const { orderIds: curPdf, title_holder } = collectOrderReportSelectionFromDeliveryRows(selectedCustomers);
                                                const title = Array.from(title_holder).join(',');
                                                if (curPdf.length > 0)
                                                    getFile(false, () => buildQuerySum(curPdf, date, date, title, true, true, undefined, undefined, 1, undefined, undefined, undefined, 4, true), 'חוסרים תעודות — לקוח ויחידות בהזמנה');
                                                hideContextMenu();
                                            }
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            name: "ייצא סיכום תעודות",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                const title = driverExportTitle(title_holder);
                                if (curPdf.length > 0)
                                    getFile(false, () => buildQuerySum(curPdf, optDate, optDate, title, true, false, undefined, undefined, 1), driverExportFileName('סיכום תעודות', title_holder, optDate));
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום נהגים",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                const title = driverExportTitle(title_holder);
                                if (curPdf.length > 0)
                                    getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, 1, undefined, undefined, title, undefined, 1), driverExportFileName('סיכום נהגים תעודות', title_holder, optDate));
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום נהגים מפורט",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                const title = driverExportTitle(title_holder);
                                if (curPdf.length > 0)
                                    getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, 2, undefined, undefined, title, undefined, 1), driverExportFileName('סיכום נהגים מפורט תעודות', title_holder, optDate));
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום תעודות מפורט כללי",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                if (curPdf.length > 0)
                                    getFile(false, () => dailyBuildQuerySum(curPdf, date, 4, 2, undefined, undefined, '', undefined, 1), driverExportFileName('סיכום תעודות מפורט כללי', title_holder, optDate));
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום נהגים בטבלה",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                const title = driverExportTitle(title_holder);
                                if (curPdf.length > 0)
                                    getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, 2, undefined, undefined, title, 10, 1), driverExportFileName('סיכום נהגים תעודות', title_holder, optDate));
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום נהגים לפי מוצר",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                const { deliveryIds: curPdf, title_holder } = collectDeliveryReportSelection(selectedCustomers);
                                const title = driverExportTitle(title_holder);
                                if (curPdf.length > 0)
                                    getFile(false, () => dailyBuildQuerySum(curPdf, date, 1, 2, undefined, undefined, title, 3, 1), driverExportFileName('סיכום נהגים לפי מוצר תעודות', title_holder, optDate), "application/pdf");
                                hideContextMenu();
                            }
                        },
                        {
                            name: "ייצא סיכום תעודות ספציפי לפי מוצר",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                setOpenSelectProductsToExport({selectedCustomers: selectedCustomers, detailed: 2, byDelivery: true});
                            }
                        },
                        {
                            name: "ייצא סיכום תעודות ספציפי",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                setOpenSelectProductsToExport({selectedCustomers: selectedCustomers, byDelivery: true});
                            }
                        },
                        {
                            name: "ייצא סיכום תעודות ספציפי מפורט",
                            icon: `<img src="${pdfIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                            action() {
                                setOpenSelectProductsToExport({selectedCustomers: selectedCustomers, detailed: 1, byDelivery: true});
                            }
                        },
                    ]
                },
            ]
        },
        {
            name: "ממשקים",
            subMenu: [
                {
                    name: "ייצא הזמנות לממשק אקסל",
                    icon: `<img src="${xslx}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const ids: any = [];
                        const update_printed = new Set();

                        selectedCustomers.forEach((data) => {
                            if (data?.orderId) ids.push(data?.orderId)
                            if (data?.orderId && !data?.readonly) update_printed.add(data?.orderId)

                        })

                        const curPdf = ids.map((u) => Number(u));

                        if (curPdf.length > 0) {
                            const from = parseJSON(date);
                            const to = parseJSON(date);
                            getFile(false, () => dataMimshak(from, to, 'nissan', undefined, curPdf,), `נתונים לממשק ${from}_${to}.zip`, 'application/octet-stream', undefined, undefined);

                            setTempData((prev) =>
                                prev.map((d, i) => {
                                        if (update_printed.has(d.orderId)) {
                                            updatedRowIds.add(orderKey(d));

                                            return {...d, status: 'הודפס', hasBeenEdited: true};
                                        } else {
                                            return d;
                                        }

                                    }
                                )
                            );
                            setTimeout(() => {
                                params.api.refreshCells({force: true});
                            }, 50)


                        }

                        hideContextMenu();
                    },
                },{
                    name: "ייצא הזמנות לממשק",
                    icon: `<img src="${boxIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const docs: any = [];

                        const update_printed = new Set();

                        selectedCustomers.forEach((data) => {
                            if (data?.dvs && data?.dvs.length > 0)
                                data?.dvs?.forEach((dd) =>  docs.push(dd));


                        })



                        if (docs.length > 0) {
                            const from = parseJSON(date);
                            const to = parseJSON(date);
                            getFile(false, () => dataToHash(from, to,docs.map((p) => p.id),docs.map((p) => p.nkObject.getConnectedDocType())), `נתונים לממשק ${from}_${to}.zip`, 'application/octet-stream', undefined, undefined);



                        }

                        hideContextMenu();
                    },
                },]
        },
        {
            name: "מסלולים",
            icon: `<img src="${boxIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
            subMenu: [
                {
                    name: "סדר לקוחות בצורה אופטימלית",
                    // icon: <GradingIcon />,
                    action: () => {

                        // const ag = JSDB().createDailyData(date,Number(rowData?.id),rowData?.orderDriver ?? rowData?.driverName,rowData?.collectorName,rowData?.position,rowData?.collector_position,rowData?.mName,-1,rowData?.car)
                        console.log(rowData?.orderDriver ?? rowData?.driverName)
                        const ag = JSDB().getAgentBy(null, rowData?.orderDriver ?? rowData?.driverName, false, true, false, false)?.first

                        const startPoint = rowData?.start_point ? rowData?.start_point :
                            JSON.parse(localStorage.getItem(`driver_${rowData?.id}_position`) || '{}')?.start_position;
                        const endPoint = rowData?.end_point ? rowData?.end_point :
                            JSON.parse(localStorage.getItem(`driver_${rowData?.id}_position`) || '{}')?.end_position;
                        const start = ag?.start_point
                        const end = ag?.end_point
                        setReorderDialog({
                            data: nodesInRange,
                            start: start,
                            end: end,
                            ag: ag,
                            api: params.api,
                        });


                    },
                },
                {
                    name: "הצג מסלול בגוגל מפות",
                    action: async () => {
                        const rows = nodesInRange.filter((d) => d?.id);
                        if (!rows.length) return;
                        const locationsArray = (await resolveRowsGeoLocations(rows)).filter((loc) => loc && loc !== '');
                        window && locationsArray.length && window.open(`https://www.google.com/maps/dir/${locationsArray.join("/")}`, '_blank')?.focus();
                    },
                    // icon: <MapPin size={22} strokeWidth={1} color="#000000" />
                },

                {
                    name: "הצג נקודות במפה חיצונית ",
                    // icon: <Map2 size={22} strokeWidth={1} color="#000000" />,
                    action: async () => {
                        const rows = nodesInRange.filter((d) => d?.id);
                        if (!rows.length) return;
                        const locationsArray = await resolveRowsGeoLocations(rows);

                        const validEntries = rows
                            .map((row, i) => ({row, loc: locationsArray[i]}))
                            .filter((entry) => entry.loc && entry.loc !== '');

                        let pointsString = "";
                        validEntries.forEach(({row, loc}, i) => {
                            const c = row?.customer?.replace(" ", "+")
                            const [lat, lon] = loc.split(",");
                            pointsString += `point=${lat}%2C${lon}_${c}`;
                            if (i !== validEntries.length - 1) {
                                pointsString += "&";
                            }
                        });
                        const url = `https://graphhopper.com/maps/?${pointsString}&profile=truck&layer=Omniscale`;
                        window.open(url, '_blank')?.focus();
                    }
                },
                ...(getRepLinkFlags().driver
                    ? [
                          {
                              name: "צור לינק נהג למסומנים",
                              action: () => {
                                  const orderRows = selectedCustomers.filter((row) =>
                                      row?.orderId != null &&
                                      row.orderId !== '' &&
                                      Number.isFinite(Number(row.orderId)) &&
                                      Number(row.orderId) > 0
                                  );
                                  if (!orderRows.length) {
                                      Swal.fire({
                                          icon: 'info',
                                          title: 'אין הזמנות במסומנים',
                                          text: 'סמן לקוחות עם הזמנות פעילות ואז צור לינק נהג.',
                                      });
                                      hideContextMenu();
                                      return;
                                  }
                                  hideContextMenu();
                                  setDriverLinkRows(orderRows);
                              },
                          },
                          {
                              name: "צור לינק נהג קבוע",
                              action: () => {
                                  hideContextMenu();
                                  setPersistentDriverLinkRows?.(selectedCustomers ?? []);
                              },
                          },
                      ]
                    : []),
                ...(routeAssignEnabled
                    ? [
                          {
                              name: "שבץ נהג זמני לפי כתובת",
                              action: async () => {
                                  const rows = selectedCustomers.filter((row) => row?.id && row?.orderId);
                                  hideContextMenu();
                                  if (!rows.length) {
                                      Swal.fire({
                                          icon: 'info',
                                          title: 'אין הזמנות במסומנים',
                                          text: 'סמן שורות עם הזמנה וכתובת, ואז שבץ נהג לפי הכתובת.',
                                      });
                                      return;
                                  }

                                  Swal.fire({
                                      title: 'משבץ נהג לפי כתובת…',
                                      text: `${rows.length} הזמנות`,
                                      allowOutsideClick: false,
                                      didOpen: () => Swal.showLoading(),
                                  });

                                  try {
                                      const assigned: {key: string; name: string; row: any}[] = [];
                                      const skipped: string[] = [];
                                      const failed: string[] = [];

                                      const results = await Promise.all(rows.map(async (row) => {
                                          const addr = getRowDeliveryAddress(row);
                                          const label = row?.customer || `#${row?.id}`;
                                          if (!addr.raw) {
                                              return {kind: 'skipped' as const, label, reason: 'אין כתובת'};
                                          }
                                          try {
                                              const res = await aiRouteAssign({
                                                  raw: addr.raw,
                                                  city: addr.city || undefined,
                                                  street: addr.street || undefined,
                                                  number: addr.number || undefined,
                                                  client_id: Number(row.id) || undefined,
                                                  order_id: String(row.orderId),
                                                  source: 'datasheet',
                                                  persist: true,
                                                  allow_llm: true,
                                              });
                                              if (res.skip || !res.agent_id || (res.confidence || 0) < ROUTE_ASSIGN_MIN_CONFIDENCE) {
                                                  return {
                                                      kind: 'skipped' as const,
                                                      label,
                                                      reason: res.reason || 'לא נמצא נהג מספיק ודאי',
                                                  };
                                              }
                                              const name = driverNameByAgentId(Number(res.agent_id));
                                              if (!name) {
                                                  return {kind: 'failed' as const, label, reason: `נהג #${res.agent_id} לא נמצא במערכת`};
                                              }
                                              return {kind: 'assigned' as const, label, name, row};
                                          } catch (e: any) {
                                              return {kind: 'failed' as const, label, reason: e?.message || String(e)};
                                          }
                                      }));

                                      for (const r of results) {
                                          if (r.kind === 'assigned') {
                                              assigned.push({key: orderKey(r.row), name: r.name, row: r.row});
                                          } else if (r.kind === 'skipped') {
                                              skipped.push(`${r.label}: ${r.reason}`);
                                          } else {
                                              failed.push(`${r.label}: ${r.reason}`);
                                          }
                                      }

                                      if (assigned.length) {
                                          const byKey = new Map(assigned.map((a) => [a.key, a.name]));
                                          const patched = assigned.map((a) => {
                                              const next = {...a.row, orderDriver: a.name, hasBeenEdited: true};
                                              next.me = next;
                                              return next;
                                          });
                                          setTempData((prev) => (prev || []).map((d) => {
                                              const name = byKey.get(orderKey(d));
                                              if (!name) return d;
                                              const next = {...d, orderDriver: name, hasBeenEdited: true};
                                              next.me = next;
                                              updatedRowIds.add(orderKey(next));
                                              updatedDriverRowIds.add(next.id);
                                              return next;
                                          }));
                                          await saveOrder(patched.map((p) => orderKey(p)), undefined, patched);
                                      }

                                      const lines = [
                                          assigned.length ? `שובץ: ${assigned.length}` : '',
                                          skipped.length ? `דולג: ${skipped.length}` : '',
                                          failed.length ? `נכשל: ${failed.length}` : '',
                                      ].filter(Boolean);
                                      const details = [
                                          ...assigned.map((a) => `${a.row?.customer || a.key} → ${a.name}`),
                                          ...skipped.slice(0, 8),
                                          ...failed.slice(0, 8),
                                      ].join('<br/>');
                                      await Swal.fire({
                                          icon: failed.length && !assigned.length ? 'error' : 'success',
                                          title: assigned.length ? 'נהג זמני עודכן' : 'לא שובץ נהג',
                                          html: `${lines.join(' · ')}${details ? `<div style="margin-top:8px;text-align:right;font-size:13px">${details}</div>` : ''}`,
                                      });
                                  } catch (e: any) {
                                      Swal.fire({
                                          icon: 'error',
                                          title: 'שיבוץ הנהג נכשל',
                                          text: e?.message || String(e),
                                      });
                                  }
                              },
                          },
                      ]
                    : []),
            ]
        },
        {
            name: "פעולות מיוחדות",
            subMenu: [
                {
                    name: AG_GRID_LOCALE_HE.copyTransposed,
                    action: () => {
                        copyTransposedSelectionToClipboard(params.api, false);
                        hideContextMenu();
                    }
                },
                {
                    name: AG_GRID_LOCALE_HE.copyTransposedWithHeaders,
                    action: () => {
                        copyTransposedSelectionToClipboard(params.api, true);
                        hideContextMenu();
                    }
                },
                "separator",
                {
                    name: "הוסף לקוחות לפילטור",
                    cssClasses: ["context-menu-blue-item"],
                    icon: `<img src="${addFilterIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const names: any = [];
                        for (let i = 0, len = selectedCustomers.length; i < len; i += 1) {
                            if (selectedCustomers[i]?.customer) names.push(selectedCustomers[i]?.customer);
                        }
                        removeAllFilters();
                        onApplyFilter({property: 'customer', contains: '', values: Array.from(names)});
                        addCustomerFilter(Array.from(names));
                        // if (current.selection)
                        //     current?.setSelection({
                        //         min: { col: current.selection.min.col, row: 0 },
                        //         max: { col: current.selection.max.col, row: names.length - 1 }
                        //     });

                    },
                },
                {
                    name: "פיזור אוטומטי",
                    icon: `<img src="${branchIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    disabled: !isProduct,
                    action() {
                        setDistributeDialogOn({
                            data: nodesInRange,
                            colName: colId,
                        })
                    }
                },
                {
                    name: "סמן עמודה",
                    icon: `<img src="${columnIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action: () => {
                        params.api.clearRangeSelection();
                        params.api.addCellRange({
                            rowStartIndex: 0,
                            rowEndIndex: params.api.getDisplayedRowCount() - 1,
                            columns: [params.api.getFocusedCell().column]
                        });
                    }
                },
                {
                    name: "סמן שורות",
                    icon: `<img src="${rowIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action: () => {
                        const startRow = range.startRow.rowIndex;
                        const endRow = range.endRow.rowIndex;
                        for (let i = startRow; i <= endRow; i++) {
                            params.api.getDisplayedRowAtIndex(i).setSelected(true);
                        }
                    }
                },

                "separator",
                {
                    name: "פתח לינק הזמנה",
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
                    action() {
                        const focusedCell = params?.api?.getFocusedCell();
                        const node = params?.api?.getDisplayedRowAtIndex(focusedCell?.rowIndex);
                        const clientId = Number(node?.data?.id ?? rowData?.id);
                        if (!clientId) return;
                        const tab = window.open('about:blank', '_blank');
                        callbackNetworkResult(
                            async () => {
                                try {
                                    return await getCofcLink(clientId, date);
                                } catch (e) {
                                    tab?.close();
                                    throw e;
                                }
                            },
                            (result) => {
                                const link = buildCofcOrderShopLink(String(result), dateObjectToString(date));
                                if (tab && !tab.closed) {
                                    tab.location.href = link;
                                    tab.focus();
                                } else {
                                    window.open(link, '_blank')?.focus();
                                }
                            }
                        );
                        hideContextMenu();
                    }
                },
                {
                    name: "שלח לינק",
                    icon:
                        `<img style="width: 18px;height: 18px" src=` + whatsapp2 + ` />`,
                    action() {
                        const ids = new Set();
                        const update_printed = new Set();

                        selectedCustomers.forEach((data) => {
                            ids.add((data?.id))
                            if (!data?.readonly) update_printed.add(orderKey(data))
                        })


                        const curPdf = Array.from(ids).map((u) => Number(u));
                        if (curPdf.length > 0) {
                            setSelectedCustomersSend({
                                selected: curPdf,
                                defaultPlatform: 'whatsapp',
                                msg: '#link',
                                subject: 'לינק  להזמנה',
                                // Orders-screen date. Server 10 (Baileys) expands #link in the
                                // WhatsApp drawer; without this it falls back to today.
                                action_date: date,
                                onSend: (res) => {
                                    const cd = []
                                    setTempData((prev) =>
                                        prev.map((d, i) => {
                                                if (update_printed.has(orderKey(d)) && d.status!="התקבלה ע''י לקוח") {
                                                    cd.push({...d, status: 'ממתין ללקוח', hasBeenEdited: true,keep:true})
                                                    updatedRowIds.add(orderKey(d));
                                                    return {...d, status: 'ממתין ללקוח', hasBeenEdited: true,keep:true};
                                                } else {
                                                    return d;
                                                }

                                            }
                                        )
                                    );

                                    // refuctor this
                                    if (JSDB().getCompany() != 'masheou' && JSDB().getCompany() != 'sendimasheou')
                                        setTimeout(() => {
                                            saveOrder(Array.from(update_printed), undefined, cd)
                                            // params.api.refreshCells({force: true});

                                        }, 500)
                                }
                            })


                        }


                        hideContextMenu();
                    }
                },
              
                {
                    name: "שלח תזכורת",
                    icon:
                        `<img style="width: 18px;height: 18px" src=` + whatsapp2 + ` />`,
                    action() {
                        const ids: any = [];
                        selectedCustomers.forEach((data) => {
                            ids.push((data?.id))

                        })

                        const curPdf = ids.map((u) => Number(u));
                        if (curPdf.length > 0)
                            setSelectedCustomersSend({
                                selected: curPdf,
                                defaultPlatform: 'whatsapp',
                                // Bake the orders-screen date in. The WhatsApp API (server 10)
                                // otherwise replaces a leftover #date with today.
                                msg: `היי לא קיבלנו הזמנה לתאריך ${dateObjectToStringIsrael(date)}`,
                                subject: 'תזכורת',
                                action_date: date,
                            })
                        hideContextMenu();
                    }
                },
                "separator",
                {
                    name: tableDividers?.includes(colId) ? "הסר הפרדה" : "הפרד עמודה לימין",
                    icon: `<img src="${borderIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const colDef = range?.startColumn.colDef;
                        if (tableDividers?.includes(colId)) {
                            const index = tableDividers.indexOf(colId);
                            tableDividers.splice(index, 1);
                            colDef.cellStyle = (params) => {
                                return {
                                    borderLeft: `1px solid ${theme.palette.divider}`,
                                    borderRight: "none",
                                    backgroundColor: params?.data?.readonly ? "#9affdd" : params?.data?.hasBeenEdited ? "#ff020240" : "inherit",
                                    fontWeight: (params?.data?.hasBeenEdited || params?.node?.rowPinned === "top") ? "bold" : "normal"
                                }
                            };
                        } else {
                            tableDividers.push(colId);
                            colDef.cellStyle = (params) => {
                                return {
                                    borderRight: "1.5px solid black",
                                    borderLeft: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: params?.data?.readonly ? "#9affdd" : params?.data?.hasBeenEdited ? "#ff020240" : "inherit",
                                    fontWeight: (params?.data?.hasBeenEdited || params?.node?.rowPinned === "top") ? "bold" : "normal"
                                }
                            }
                        }
                        localStorage.setItem('orders-table-dividers', JSON.stringify(tableDividers));
                        const col = params.columnApi.getColumn(range?.startColumn.colId);
                        params?.api?.refreshCells({columns: [col], force: true});
                    }
                },
                {
                    name: "הצג מיקום לקוח",
                    icon: `<img src="${pinIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const userLocation = JSDB().getClient(Number(rowData?.id))?.first?.location;
                        if (userLocation) {
                            window.open(`https://www.google.com/maps/search/?api=1&query=${userLocation}`, '_blank')?.focus();
                        }
                    }
                },
                {
                    name: "התקשר ללקוח",
                    icon: `<img src="${phoneIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>`,
                    action() {
                        const focusedCell = params?.api?.getFocusedCell();
                        const node = params?.api?.getDisplayedRowAtIndex(focusedCell?.rowIndex);
                        const client = JSDB().getClient(Number(node?.data?.id)).first;
                        const openPhone = (result) => {
                            window.open(`tel:${result}`, '_blank')?.focus();
                        };
                        if (client?.phone) {
                            const hasTwo = client?.phone?.includes(',');
                            if (hasTwo[1]) {
                                const phones = client?.phone?.split(',');
                                Swal.fire({
                                    title: 'בחר מספר',
                                    input: 'select',
                                    inputOptions: {
                                        '0': phones[0],
                                        '1': phones[1],
                                    },
                                    inputPlaceholder: 'בחר מספר',
                                    showCancelButton: true,
                                    inputValidator: (value) => {
                                        if (!value) {
                                            return 'עליך לבחור מספר';
                                        }
                                    }
                                } as any).then((result) => {
                                    if (result.isConfirmed) {
                                        openPhone(phones[result.value]);
                                    }
                                });
                            } else {
                                openPhone(client?.phone);
                            }
                        }
                    }
                },
            ]
        },
        "separator",
        {
            name: "פעולות להזמנות",
            icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
            subMenu: [
                {

                    name: `צור הזמנה נוספת`,
                    disabled: selectedCustomers.length > 1,
                    icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
                    cssClasses: ["context-menu-blue-item"],
                    action() {
                        newClientOrder(selectedCustomers);
                        hideContextMenu();
                    },
                },
                "separator",

                {
                    name: "העתק ללקוח אחר",
                    icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
                    cssClasses: ["context-menu-blue-item"],
                    action() {
                        setClientTransferAction({ docs: selectedCustomers, action: 'copy' });
                        hideContextMenu();
                    },
                },
                {
                    name: "העבר ללקוח אחר",
                    disabled: selectedCustomers.some((d) => d.readonly || !d.orderId || d.orderId === ''),
                    icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
                    cssClasses: ["context-menu-red-item"],
                    action() {
                        setClientTransferAction({ docs: selectedCustomers, action: 'move' });
                        hideContextMenu();
                    },
                },
                "separator",
                {

                    name: `שכפל לפה  ${selectedCustomers.length} לקוחות`,
                    icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,

                    action() {
                        const clickRect = document.querySelector('.ag-menu-list')?.getBoundingClientRect();
                        if (!clickRect?.right && !clickRect?.top) return;
                        const closestCell = document.elementFromPoint(clickRect.right + 10, clickRect.top + 10) as HTMLElement;
                        if (!closestCell) return;
                        setExternalAnchor(closestCell);
                        setIdsForDuplicate(selectedCustomers);
                    },
                },
                // {
                //     name: `שכפל לפה רק את התאים שנבחרו`,
                //     icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
                //     action() {
                //
                //         hideContextMenu();
                //     },
                // },
                "separator",
                {
                    name: "העבר הזמנות לתאריך אחר",
                    action() {
                        const goodNodes = nodesInRange
                        if (goodNodes.length > 0)
                            setDocumentAction({
                                docs: goodNodes,
                                action: "move",
                                doc_type: "order",
                                title: "העבר הזמנות לתאריך אחר",
                            })
                        hideContextMenu();
                    },
                    disabled: false
                },
                {
                    name: "שכפל הזמנה לתאריך אחר",
                    icon: `<img style="width: 18px;height: 18px" src=` + duplicateIcon + ` />`,
                    action() {
                        const goodNodes = nodesInRange.filter((d) => d?.orderId != undefined && d?.orderId !== '');
                        if (goodNodes.length > 0)
                            setDocumentAction({
                                docs: goodNodes,
                                action: "duplicate",
                                doc_type: "order",
                                title: "שכפל הזמנה לתאריך אחר",
                            })
                        hideContextMenu();
                    },
                    disabled: selectedCustomers.every((d) => !d?.orderId || d?.orderId === ''),
                },
                {
                    name: "שכפל לאחר חלוקה ליום אחר",
                    action() {
                        console.log(pinnedControl)
                        const goodNodes = nodesInRange
                        const filterdNodeData: any = [];
                        params?.api?.forEachNodeAfterFilterAndSort((node) => {
                            node.data && filterdNodeData.push(node.data);
                        });
                        let r  = {id:"-3"}
                        console.log(filterdNodeData)
                        const conf1 = pinnedControl?.pinnedRowControlsNew.find((c1) => c1.id === 12);
                        const conf2 = pinnedControl?.pinnedRowControlsNew.find((c1) => c1.id === 1);

                        let r1;
                        let r2;
                        if (conf1!.editableData) {
                            r1 = conf1!.editableData;
                        } else {
                            r1 = createPinnedRowDataNew(columns, filterdNodeData, conf1,true);
                        }
                        if (conf2!.editableData) {
                            r2 = conf2!.editableData;
                        } else {
                            r2 = createPinnedRowDataNew(columns, filterdNodeData, conf2,true);
                        }
                        Object.keys(r1).forEach((k) => {
                            if (!isNaN(Number(r1[k])) && !isNaN(Number(r2[k])))
                                r[k] = (Number(r1[k]) ?? 0) - (Number(r2[k]) ?? 0);
                            else r[k] = '';
                            }
                        );
                        console.log(r)
                        r.id="-3"
                        r.orderId=''
                        const x = deserializeOrderDataToOrder(r)

                        if (goodNodes.length > 0)
                            setDocumentAction({
                                docs: x,
                                action: "duplicate",
                                doc_type: "order_inventory",
                                title: "שכפל לאחר חלוקה ליום אחר"
                            })
                        hideContextMenu();
                    },
                    disabled: false
                },

                {
                    name: "פריז הזמנה",
                    disabled: freezeableSelectedCustomers.length === 0,
                    icon: `<img style="width: 18px;height: 18px" src=` + pinIcon + ` />`,
                    action() {
                        const orders: any = freezeableSelectedCustomers;
                        const text = orders.length === 1 ? `האם תרצה להקפיא מצב הסטוריה ללקוח ${orders[0]?.customer}?` : `האם תרצה להקפיא מצב הסטוריה ל-${orders.length} הזמנות פעילות?`
                        Swal.fire({
                            title: text,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: theme.palette.primary.main,
                            cancelButtonColor: theme.palette.error.main,
                            confirmButtonText: 'פריז',
                            cancelButtonText: 'ביטול פעולה'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                /* Perform insert, update, and delete to the database here */

                                orders.forEach((d) => {
                                    updatedRowIds.add(orderKey(d));
                                })
                                setTempData((prev) =>
                                    prev.map((d, i) => {
                                            if (orders.some((order) => orderKey(order) === orderKey(d))) {


                                                return {...d, hasBeenEdited: true};
                                            } else {
                                                return d;
                                            }

                                        }
                                    )
                                );
                                setTimeout(() => {
                                    saveOrder(orders.map((p) => orderKey(p)), undefined,undefined,true);
                                    // params.api.refreshCells({force: true});

                                }, 500)

                            }
                        });
                    }
                },
            ]
        },
        {
            name: "מחק הזמנה",
            disabled: rowData?.readonly || rowData?.orderId == undefined || rowData?.orderId == '',
            icon: `<img style="width: 18px;height: 18px" src=` + deleteIcon + ` />`,
            action() {
                const text = selectedCustomers?.length === 1 ? `האם תרצה למחוק הזמנה ללקוח ${rowData?.customer}?` : `האם תרצה למחוק ${selectedCustomers?.length} הזמנות?`;
                Swal.fire({
                    title: text,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: theme.palette.primary.main,
                    cancelButtonColor: theme.palette.error.main,
                    confirmButtonText: 'מחק',
                    cancelButtonText: 'ביטול פעולה'
                }).then((result) => {
                    if (result.isConfirmed) {
                        /* Perform insert, update, and delete to the database here */
                        const orders: any = [];
                        selectedCustomers.forEach((data) => {
                            if (!data?.readonly && data?.orderId != undefined && data.orderId != '' && data.status != 'בוצעה') orders.push(data.orderId);

                        })
                        deleteOrder(orders);
                    }
                });
            }
        },
        {
            name: "בטל שינויים בהזמנה",
            icon: `<img style="width: 18px;height: 18px" src=` + cancelIcon + ` />`,
            action() {
                const orders: any = [];
                selectedCustomers.forEach((data) => {
                    // Include closed orders so force_position / נהג זמני edits can be reverted too.
                    orders.push(orderKey(data));
                })
                setSingleDelete(orders);
            }
        },
        {
            icon:
                '<img style="width: 18px;height: 18px" src="https://cdn1.iconfinder.com/data/icons/ios-11-glyphs/30/save-512.png" />',
            // Allow save on closed (readonly) orders so סדר נהגים / force_position / נהג זמני can still be persisted.
            disabled: dvs_flag || !rowData || (!updatedRowIds.has(orderKey(rowData)) && !updatedDriverRowIds.has(rowData.id) && !rowData.hasBeenEdited),
            name: "שמור",
            action() {
                const orders: any = [];
                selectedCustomers.forEach((data) => {
                    orders.push(orderKey(data));
                })
                saveOrder(orders);
            }
        },
    ]

    if (range?.columns?.length === 1 && (total > 0 || boxes > 0) && isProduct) {
        contextMenu.splice(1, 0, {
            name: withBoxText,
            icon: withBox ? `<img src="${boxIcon}" style="width: 20px; height: 20px; margin-right: 5px;"/>` : ``
        })
    }
    return contextMenu
}

export default contextMenu2;
